import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ShieldAlert, Database, CheckCircle2, Loader2, LogOut, RefreshCw,
  Trash2, Download, AlertTriangle, History,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

/**
 * /admin/factory-reset — Admin-only page to wipe test data before going live.
 *
 * UX flow:
 *   1. Load → fetches `/admin/factory-reset/preview` to show counts.
 *   2. User reviews what will be wiped vs preserved.
 *   3. User can optionally run a Dry-Run first (deletes nothing).
 *   4. For live wipe, user must type the exact confirmation phrase.
 *   5. On success, counts are refreshed and the backup prefix is shown
 *      so rollback is possible.
 */
export default function FactoryReset() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phrase, setPhrase] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [history, setHistory] = useState([]);

  const bootstrap = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/me`);
      if (r.data?.role !== "admin") { nav("/login"); return; }
      setMe(r.data);
    } catch { nav("/login"); }
  }, [nav]);

  const refreshPreview = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const [p, h] = await Promise.all([
        axios.get(`${API}/admin/factory-reset/preview`),
        axios.get(`${API}/admin/factory-reset/audit?limit=10`).catch(() => ({ data: { runs: [] } })),
      ]);
      setPreview(p.data);
      setHistory(h.data?.runs || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not load preview.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);
  useEffect(() => { if (me) refreshPreview(); }, [me, refreshPreview]);

  const run = async (dryRun) => {
    setErr(""); setResult(null);
    if (!dryRun && phrase !== (preview?.confirmation_phrase_required || "")) {
      setErr(`Confirmation phrase must match exactly: "${preview?.confirmation_phrase_required}"`);
      return;
    }
    if (!dryRun && !window.confirm(
      "This will DELETE all demo and test data in this environment.\n\n" +
      "A full backup will be snapshotted first and the audit log records this run.\n\n" +
      "Proceed?"
    )) return;

    setRunning(true);
    try {
      const r = await axios.post(`${API}/admin/factory-reset/execute`, {
        confirmation_phrase: preview?.confirmation_phrase_required,
        dry_run: !!dryRun,
      });
      setResult(r.data);
      setPhrase("");
      await refreshPreview();
    } catch (e) {
      setErr(e.response?.data?.detail || "Reset failed.");
    } finally { setRunning(false); }
  };

  const logout = async () => { try { await axios.post(`${API}/auth/logout`); } catch {} nav("/login"); };

  if (!me) return <div className="mat-boot"><Loader2 className="mat-spin"/> Loading…</div>;

  const phraseMatches = preview && phrase === preview.confirmation_phrase_required;
  const totalWipeDocs = preview ? Object.values(preview.counts.wipe).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className="mat-shell" data-testid="factory-reset-page">
      <header className="mat-nav">
        <Link to="/" className="m-brand"><img src="/logo.png" alt="GoPrintPack" className="m-brand-logo"/></Link>
        <div className="mat-nav-mid">
          <span className="mat-chip" style={{background:"#FEE2E2",color:"#991B1B"}}>ADMIN · DANGER ZONE</span>
          <h1 className="mat-nav-title">Factory Reset — wipe test data before go-live</h1>
        </div>
        <div className="mat-nav-actions">
          <button className="m-ghost" onClick={refreshPreview}><RefreshCw size={14}/> Refresh</button>
          <Link to="/workspace" className="m-ghost">Workspace</Link>
          <button className="m-ghost" onClick={logout}><LogOut size={14}/> Sign out</button>
        </div>
      </header>

      {err && <div className="mat-err" data-testid="factory-reset-err">{err}</div>}

      <div className="fr-shell">
        <section className="fr-callout">
          <AlertTriangle size={22}/>
          <div>
            <h3>Read before you click</h3>
            <ul>
              <li>Every wiped collection is <b>snapshotted to <code>_backup_&lt;ts&gt;__&lt;coll&gt;</code></b> before deletion. Rollback is possible.</li>
              <li>Admin accounts, HSN/SAC master data, counters, and audit logs are <b>never touched</b>.</li>
              <li>You must type the exact phrase <code>{preview?.confirmation_phrase_required || "..."}</code> to execute.</li>
              <li>Run a <b>Dry Run</b> first — it reports counts without deleting anything.</li>
            </ul>
          </div>
        </section>

        {loading
          ? <div className="mat-boot"><Loader2 className="mat-spin"/> Loading preview…</div>
          : preview && (
            <>
              <section className="fr-grid">
                <article className="fr-card fr-card-wipe" data-testid="fr-wipe-card">
                  <header><Trash2 size={16}/> Will be wiped</header>
                  <div className="fr-bignum">{totalWipeDocs.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</div>
                  <p>documents across {Object.keys(preview.counts.wipe).length} collections</p>
                  <details>
                    <summary>Breakdown ({Object.keys(preview.counts.wipe).length} collections)</summary>
                    <ul className="fr-collist">
                      {Object.entries(preview.counts.wipe)
                        .sort((a,b) => b[1] - a[1])
                        .map(([k,v]) => (
                          <li key={k}><span>{k}</span><b>{v.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</b></li>
                        ))}
                    </ul>
                  </details>
                </article>

                <article className="fr-card fr-card-users" data-testid="fr-users-card">
                  <header><Database size={16}/> Users</header>
                  <div className="fr-bignum">{preview.counts.users.will_be_wiped}</div>
                  <p>will be wiped · <b>{preview.counts.users.admins_preserved} admin</b> preserved</p>
                  <p className="fr-sub">Total before: {preview.counts.users.total}</p>
                </article>

                <article className="fr-card fr-card-safe" data-testid="fr-safe-card">
                  <header><CheckCircle2 size={16}/> Preserved</header>
                  <div className="fr-bignum">{Object.keys(preview.counts.preserve).length}</div>
                  <p>collections untouched (seed + audit)</p>
                  <details>
                    <summary>What stays</summary>
                    <ul className="fr-collist">
                      {Object.entries(preview.counts.preserve).map(([k,v]) => (
                        <li key={k}><span>{k}</span><b>{v}</b></li>
                      ))}
                    </ul>
                  </details>
                </article>
              </section>

              <section className="fr-action">
                <h3>Execute reset</h3>
                <label className="pf-field">
                  <span>Type the confirmation phrase exactly</span>
                  <input
                    type="text" value={phrase}
                    onChange={e => setPhrase(e.target.value)}
                    placeholder={preview.confirmation_phrase_required}
                    className={phraseMatches ? "fr-ok" : ""}
                    data-testid="factory-reset-phrase"
                  />
                </label>
                <div className="fr-btnrow">
                  <button
                    className="m-ghost" onClick={() => run(true)}
                    disabled={running}
                    data-testid="factory-reset-dry-run"
                  >
                    {running ? <Loader2 size={14} className="mat-spin"/> : <ShieldAlert size={14}/>}
                    Dry run (no delete)
                  </button>
                  <button
                    className="fr-btn-danger" onClick={() => run(false)}
                    disabled={running || !phraseMatches}
                    data-testid="factory-reset-execute"
                  >
                    {running ? <Loader2 size={14} className="mat-spin"/> : <Trash2 size={14}/>}
                    Execute reset
                  </button>
                </div>
              </section>

              {result && (
                <section className="fr-result" data-testid="factory-reset-result">
                  <h3>
                    <CheckCircle2 size={18}/>
                    {result.dry_run ? "Dry run complete" : "Reset complete"}
                  </h3>
                  <div className="fr-result-row">
                    <span>Backup prefix</span>
                    <code>{result.backup_prefix}</code>
                  </div>
                  <div className="fr-result-row">
                    <span>Users wiped</span>
                    <b>{result.users_wiped}</b>
                    <span style={{marginLeft:12}}>backed up</span>
                    <b>{result.users_backed_up}</b>
                  </div>
                  <div className="fr-result-row">
                    <span>Audit id</span>
                    <code>{result.audit_id}</code>
                  </div>
                  <details>
                    <summary>Per-collection breakdown</summary>
                    <ul className="fr-collist">
                      {result.wiped_summary.map((w, i) => (
                        <li key={i}>
                          <span>{w.collection}</span>
                          <b>{w.wiped} wiped · {w.backed_up} saved</b>
                        </li>
                      ))}
                    </ul>
                  </details>
                </section>
              )}

              {history.length > 0 && (
                <section className="fr-history" data-testid="factory-reset-history">
                  <h3><History size={16}/> Recent runs</h3>
                  <ul>
                    {history.map(h => (
                      <li key={h.id}>
                        <b>{h.id}</b>
                        <span>{h.dry_run ? " (dry-run)" : ""}</span>
                        <span>· by {h.performed_by}</span>
                        <span>· {new Date(h.performed_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</span>
                        <span>· {h.users_wiped} users wiped</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )
        }
      </div>

      <footer className="mat-foot">
        Need help? <a href="mailto:info@goprintpack.com">info@goprintpack.com</a>
      </footer>
    </div>
  );
}
